/**
 * OutcomeFollowups — N5 utfallsuppföljning (30/90/180 dagar).
 *
 * Visas i deltagarens 360-vy enbart när status === 'Avslutad'.
 * Skapas automatiskt av backend när deltagaren avslutas.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { CalendarClock, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const TYPE_LABEL = {
  '30_days':  '30-dagarsuppföljning',
  '90_days':  '90-dagarsuppföljning',
  '180_days': '180-dagarsuppföljning',
};

const OUTCOME_OPTIONS = [
  { value: 'kvarstår',     label: 'Kvarstår' },
  { value: 'avbruten',     label: 'Avbruten' },
  { value: 'okänd',        label: 'Okänd' },
  { value: 'ej_kontaktbar', label: 'Ej kontaktbar' },
];

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('sv-SE'); } catch { return iso; }
};

const todayIso = () => new Date().toISOString().slice(0, 10);


const FollowupBadge = ({ followup }) => {
  if (followup.status === 'completed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-success/15 text-success">
        <CheckCircle2 size={12} /> Genomförd {formatDate(followup.completed_date)}
      </span>
    );
  }
  const today = todayIso();
  if (followup.scheduled_date && followup.scheduled_date < today) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
        <AlertCircle size={12} /> Försenad — skulle ha gjorts {formatDate(followup.scheduled_date)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-muted text-muted-foreground">
      <Clock size={12} /> Väntar — förfaller {formatDate(followup.scheduled_date)}
    </span>
  );
};


const FollowupCard = ({ followup, participantId, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    outcome_status: '',
    completed_date: todayIso(),
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.outcome_status) {
      toast.error('Välj utfall');
      return;
    }
    setSaving(true);
    try {
      await api.patch(
        `/participants/${participantId}/outcome-followups/${followup.id}`,
        {
          status: 'completed',
          outcome_status: form.outcome_status,
          completed_date: form.completed_date || todayIso(),
          notes: form.notes || null,
        },
      );
      toast.success('Uppföljning registrerad');
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-l-4 border-l-primary" data-testid={`outcome-followup-${followup.followup_type}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <CalendarClock size={20} className="text-primary" />
            <div>
              <p className="font-bold text-sm">{TYPE_LABEL[followup.followup_type] || followup.followup_type}</p>
              <p className="text-xs text-muted-foreground">Schemalagt: {formatDate(followup.scheduled_date)}</p>
            </div>
          </div>
          <FollowupBadge followup={followup} />
        </div>

        {followup.status === 'completed' && (
          <div className="mt-3 text-sm space-y-1">
            <div>
              <span className="text-muted-foreground">Utfall: </span>
              <span className="font-semibold">
                {OUTCOME_OPTIONS.find((o) => o.value === followup.outcome_status)?.label || followup.outcome_status}
              </span>
            </div>
            {followup.notes && (
              <div className="text-muted-foreground italic">{followup.notes}</div>
            )}
          </div>
        )}

        {followup.status !== 'completed' && !editing && (
          <div className="mt-3">
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => setEditing(true)}
              data-testid={`register-followup-${followup.followup_type}`}
            >
              Registrera uppföljning
            </Button>
          </div>
        )}

        {editing && (
          <form onSubmit={handleSubmit} className="mt-4 space-y-3 border-t pt-4">
            <div className="space-y-1.5">
              <Label htmlFor={`outcome-${followup.id}`}>Utfall <span className="text-destructive">*</span></Label>
              <select
                id={`outcome-${followup.id}`}
                value={form.outcome_status}
                onChange={(e) => setForm((p) => ({ ...p, outcome_status: e.target.value }))}
                className="h-10 w-full px-3 border rounded-md bg-background text-sm"
                data-testid={`outcome-status-select-${followup.followup_type}`}
              >
                <option value="">Välj utfall...</option>
                {OUTCOME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`date-${followup.id}`}>Datum</Label>
              <Input
                id={`date-${followup.id}`}
                type="date"
                value={form.completed_date}
                onChange={(e) => setForm((p) => ({ ...p, completed_date: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`notes-${followup.id}`}>Anteckning</Label>
              <Textarea
                id={`notes-${followup.id}`}
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                rows={2}
                placeholder="Valfritt..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" className="rounded-full" onClick={() => setEditing(false)} disabled={saving}>
                Avbryt
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-full font-bold"
                style={{ background: '#F9CF3F', color: '#333333' }}
                data-testid={`save-followup-${followup.followup_type}`}
              >
                {saving ? 'Sparar...' : 'Spara'}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};


const OutcomeFollowups = ({ participantId }) => {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const res = await api.get(`/participants/${participantId}/outcome-followups`);
      setFollowups(res.data || []);
    } catch {
      setFollowups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (participantId) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId]);

  if (loading) return null;
  if (!followups.length) return null;

  return (
    <Card data-testid="outcome-followups">
      <CardContent className="p-5 space-y-3">
        <div>
          <h3 className="text-base font-heading font-bold">Utfallsuppföljning</h3>
          <p className="text-xs text-muted-foreground">
            Kontroll om utfallet kvarstår efter 30, 90 och 180 dagar.
          </p>
        </div>
        <div className="space-y-3">
          {followups.map((f) => (
            <FollowupCard
              key={f.id}
              followup={f}
              participantId={participantId}
              onSaved={fetchData}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default OutcomeFollowups;
